import {
  Article,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle
} from "@babackapp/customer-ui/components/article";
import { SectionHeader } from "@babackapp/customer-ui/components/header";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import type { CartItem } from "@babackapp/return-widget-components/types/shop-now";
import { Separator } from "@babackapp/ui/components/separator";
import { CompleteReturnLineItem } from "./complete-return-line-item";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";

type Props = {
  orderId: string;
  cart: { items: CartItem[]; currency: string } | null;
};

export function CompleteSummary({ orderId, cart }: Props) {
  const { data: order } = usePublicOrder(orderId);
  const draft = useDraftReturn(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const t = useCustomText();

  const returnItems = draft?.lineItems ?? [];
  const exchangeItems = returnItems.filter(
    (li) => li.resolution === "exchange" || li.resolution === "exchange-credit",
  );
  const refundItems = returnItems.filter((li) => li.resolution === "refund");
  const discountItems = returnItems.filter((li) => li.resolution === "discount");
  const orderLineItems = order?.lineItems ?? [];

  const cartTotal = cart ? cart.items.reduce((sum, i) => sum + i.price * i.quantity, 0) : 0;
  const credit =
    financialSummary.refundTotal + financialSummary.exchangeTotal + financialSummary.discountTotal;
  const net = cartTotal - credit;

  return (
    <div className="w-full mt-4 flex flex-col gap-6">
      {exchangeItems.length > 0 && (
        <SummarySection title={t("summary.exchangeItems")}>
          {exchangeItems.map((item) => (
            <CompleteReturnLineItem
              key={item.lineItemId}
              item={item}
              orderLineItems={orderLineItems}
              currency={financialSummary.currency}
            />
          ))}
        </SummarySection>
      )}
      {refundItems.length > 0 && (
        <SummarySection title={t("summary.refundItems")}>
          {refundItems.map((item) => (
            <CompleteReturnLineItem
              key={item.lineItemId}
              item={item}
              orderLineItems={orderLineItems}
              currency={financialSummary.currency}
            />
          ))}
        </SummarySection>
      )}
      {discountItems.length > 0 && (
        <SummarySection title={t("summary.discountItems")}>
          {discountItems.map((item) => (
            <CompleteReturnLineItem
              key={item.lineItemId}
              item={item}
              orderLineItems={orderLineItems}
              currency={financialSummary.currency}
            />
          ))}
        </SummarySection>
      )}
      {cart && cart.items.length > 0 && (
        <SummarySection title={t("completeSummary.newItems")}>
          {cart.items.map((item, i) => (
            <Article key={`${item.variantId}-${i}`}>
              {item.image && (
                <ArticleImage src={item.image} layoutId={`img-shop-now-complete-${item.variantId}`} />
              )}
              <ArticleTitle>{item.title}</ArticleTitle>
              <ArticleSubtitle>
                ×{item.quantity} · {formatPrice(item.price * item.quantity, cart.currency)}
              </ArticleSubtitle>
            </Article>
          ))}
        </SummarySection>
      )}

      <Separator />

      {financialSummary.refundTotal > 0 && (
        <ReceiptLine>
          <ReceiptLineLabel>{t("financial.refund")}</ReceiptLineLabel>
          <ReceiptLineValue>
            {formatPrice(financialSummary.refundTotal, financialSummary.currency)}
          </ReceiptLineValue>
        </ReceiptLine>
      )}
      {financialSummary.exchangeTotal > 0 && (
        <ReceiptLine>
          <ReceiptLineLabel>{t("financial.exchangeCredit")}</ReceiptLineLabel>
          <ReceiptLineValue>
            {formatPrice(financialSummary.exchangeTotal, financialSummary.currency)}
          </ReceiptLineValue>
        </ReceiptLine>
      )}
      {financialSummary.discountTotal > 0 && (
        <ReceiptLine>
          <ReceiptLineLabel>{t("financial.discount")}</ReceiptLineLabel>
          <ReceiptLineValue>
            {formatPrice(financialSummary.discountTotal, financialSummary.currency)}
          </ReceiptLineValue>
        </ReceiptLine>
      )}

      {(cartTotal > 0 || credit > 0) && (
        <>
          {cartTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("completeSummary.newItemsLabel")}</ReceiptLineLabel>
              <ReceiptLineValue>−{formatPrice(cartTotal, cart!.currency)}</ReceiptLineValue>
            </ReceiptLine>
          )}
          <Separator />
          <ReceiptLine>
            <ReceiptLineLabel className="font-semibold text-foreground">
              {net > 0 ? t("completeSummary.remainingToPay") : net < 0 ? t("completeSummary.netRefund") : t("completeSummary.balance")}
            </ReceiptLineLabel>
            <ReceiptLineValue className="font-semibold text-foreground">
              {formatPrice(Math.abs(net), financialSummary.currency)}
            </ReceiptLineValue>
          </ReceiptLine>
        </>
      )}
    </div>
  );
}

function SummarySection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <SectionHeader>{title}</SectionHeader>
      <ArticleList size="sm">{children}</ArticleList>
    </div>
  );
}
